import { z } from "zod";
import { formUploadAnswerSchema } from "./intake-template";

// TES-1125 — earlier blood-test results in F-02 for every NEW_TREATMENT
// patient, and the doctor's four-state status for them.
//
// Emelie 2026-10-06 (TES-475, via Simon): if she answers that she has given
// blood samples before, she may OPTIONALLY give the date and the provider and
// upload a photo or PDF; every follow-up is optional and F-02 is never
// blocked. A yes without a file is information, not a risk flag.
//
// ONE LAB-EVIDENCE MODEL. F-02 v13 (TES-1116) asked a second, low-BMI-only
// lab question ("lowbmi_labs") with its own date and provider. From intake
// v14 the general question owns all of it — whether she has results, the
// date, the provider and the file — for every new-treatment patient, and the
// low-BMI section keeps only Kirill's lab VALUES on top of it. A v13
// submission is still read: the readers below fall back to the v13 keys.
//
// TES-1136 (Ö-357, Emelie 2026-10-06) — intake v15 keeps the same keys and
// option values and changes what the questions say and when they show:
// "Har du lämnat blodprov tidigare?" Ja / Nej / Jag vet inte, then after Ja
// "Har du tillgång till resultatet?" Ja / Nej, jag har inte provsvaret /
// Jag vet inte, and the date, provider and file only after that Ja. So v13,
// v14 and v15 answers are all read by the same code. A fifth, neutral
// status says she is unsure whether earlier results exist.
//
// TES-1163 (Emelie 2026-10-07) — intake v16, same keys and values again:
// "Har du lämnat blodprov under de senaste sex månaderna?" Ja / Nej / Jag vet
// inte, then "Har du tillgång till provsvaret?" Ja / Nej / Jag vet inte.
// Of a Ja without an available or uploaded result: "Det är inte en
// riskflagga, inte ett saknat obligatoriskt underlag och inte en medicinsk
// verifiering." Nothing here raises a flag or a missing item.

/** The template the F-02 upload belongs to (FormUpload.templateKey). */
export const EARLIER_LAB_TEMPLATE_KEY = "intake";

/** F-02 answer keys of the general lab-evidence block, from intake v14. */
export const EARLIER_LAB_FIELDS = {
  /** Has she given blood samples before (no / yes / unsure); since v3. */
  stated: "med_recent_labs",
  /**
   * After a yes: does she have the results (yes / none / does not know).
   * v14: "Har du provsvar …?"; v15: "Har du tillgång till resultatet?";
   * v16: "Har du tillgång till provsvaret?".
   */
  results: "med_recent_labs_results",
  sampledOn: "med_recent_labs_sampled_on",
  provider: "med_recent_labs_provider",
  /** The file_upload field: a photo or a PDF. Rendered from app 2.0.6. */
  upload: "med_recent_labs_upload",
} as const;

/**
 * The v13 low-BMI keys the general block replaces. Literal here rather than
 * imported from low-bmi-assessment.ts, which imports this file; a contracts
 * spec pins that the two agree.
 */
export const V13_LOW_BMI_LAB_KEYS = {
  results: "lowbmi_labs",
  sampledOn: "lowbmi_lab_sampled_on",
  provider: "lowbmi_lab_provider",
} as const;

export const EARLIER_LAB_STATED_VALUES = ["ja", "nej", "osaker"] as const;
export type EarlierLabStated = (typeof EARLIER_LAB_STATED_VALUES)[number];

/**
 * The options of the results question. The values are S3's and unchanged;
 * v15 relabels "inga_provsvar" as "Nej, jag har inte provsvaret", v16 as "Nej".
 */
export const EARLIER_LAB_RESULT_VALUES = [
  "ja",
  "inga_provsvar",
  "vet_inte",
] as const;
export type EarlierLabResult = (typeof EARLIER_LAB_RESULT_VALUES)[number];

export interface EarlierLabAnswers {
  stated: EarlierLabStated | null;
  results: EarlierLabResult | null;
  sampledOn: string | null;
  provider: string | null;
  /** Upload ids her F-02 answer names, in the order she attached them. */
  uploadIds: string[];
}

function oneOf<T extends string>(values: readonly T[], raw: unknown): T | null {
  return (values as readonly unknown[]).includes(raw) ? (raw as T) : null;
}

function text(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

/** What one F-02 answer set says about earlier lab results (v13 and v14). */
export function earlierLabAnswers(
  answers: Record<string, unknown>,
): EarlierLabAnswers {
  const F = EARLIER_LAB_FIELDS;
  const V13 = V13_LOW_BMI_LAB_KEYS;
  const uploads = formUploadAnswerSchema.safeParse(answers[F.upload]);
  return {
    stated: oneOf(EARLIER_LAB_STATED_VALUES, answers[F.stated]),
    results:
      oneOf(EARLIER_LAB_RESULT_VALUES, answers[F.results]) ??
      oneOf(EARLIER_LAB_RESULT_VALUES, answers[V13.results]),
    sampledOn: text(answers[F.sampledOn]) ?? text(answers[V13.sampledOn]),
    provider: text(answers[F.provider]) ?? text(answers[V13.provider]),
    uploadIds: uploads.success ? uploads.data : [],
  };
}

/**
 * She said earlier results may exist: "Ja" to having given blood samples,
 * or (v13's low-BMI question) "Ja" to having results.
 */
export function earlierLabsMayExist(a: EarlierLabAnswers): boolean {
  return a.stated === "ja" || a.results === "ja";
}

/**
 * TES-1136 (Ö-357 a) — she does not know whether earlier results exist:
 * "Jag vet inte" to having given blood samples (`osaker`), or (v13's
 * low-BMI question, asked on its own) to having results. Not the same as
 * saying nothing. A "Ja" to having given samples wins: then results exist
 * somewhere, whatever she knows about her access to them.
 */
export function earlierLabsUnsure(a: EarlierLabAnswers): boolean {
  if (earlierLabsMayExist(a)) return false;
  return a.stated === "osaker" || a.results === "vet_inte";
}

// --- the doctor's status --------------------------------------------------------

export const EARLIER_LAB_STATUSES = [
  "not_stated",
  // TES-1136 (Ö-357 a) — "Jag vet inte". Neutral, like may_exist.
  "unsure",
  "may_exist",
  "uploaded",
  "assessed",
] as const;
export const earlierLabStatusSchema = z.enum(EARLIER_LAB_STATUSES);
export type EarlierLabStatus = z.infer<typeof earlierLabStatusSchema>;

// Emelie 2026-10-06, verbatim. Staff copy; shown NEUTRALLY — a yes without a
// file is information, not missing required evidence.
export const EARLIER_LAB_STATUS_LABELS: Record<EarlierLabStatus, string> = {
  not_stated: "Patienten har inte uppgett tidigare provsvar.",
  unsure: "Patienten är osäker på om tidigare provsvar finns.",
  may_exist:
    "Patienten uppger att tidigare provsvar kan finnas – ingen fil uppladdad.",
  uploaded: "Tidigare provsvar är uppladdat – inte medicinskt bedömt.",
  assessed: "Tidigare provsvar är bedömt av läkare.",
};

/**
 * The status from what she said and the earlier-lab files on record.
 *
 * Uploaded is not assessed: only when the doctor has marked EVERY earlier-lab
 * file assessed does it read "bedömt av läkare". A file she adds afterwards
 * brings it back to "inte medicinskt bedömt", because nobody has looked at it.
 */
export function deriveEarlierLabStatus(input: {
  mayExist: boolean;
  /** earlierLabsUnsure; ignored when mayExist or a file is on record. */
  unsure?: boolean;
  files: number;
  unassessedFiles: number;
}): EarlierLabStatus {
  if (input.files > 0) {
    return input.unassessedFiles === 0 ? "assessed" : "uploaded";
  }
  if (input.mayExist) return "may_exist";
  return input.unsure ? "unsure" : "not_stated";
}

/** The status block of "Inskickat medicinskt underlag" (EvidenceSummary). */
export const earlierLabEvidenceSchema = z.object({
  status: earlierLabStatusSchema,
  /** Her answer to "Har du lämnat blodprov …?" (v16: "under de senaste sex månaderna"), from her F-02. */
  stated: z.enum(EARLIER_LAB_STATED_VALUES).nullable(),
  /** Her answer to "Har du provsvar …?" (v14), "Har du tillgång till resultatet?" (v15) or "Har du tillgång till provsvaret?" (v16). */
  results: z.enum(EARLIER_LAB_RESULT_VALUES).nullable(),
  /** The date and provider she gave in F-02; each may be missing. */
  sampledOn: z.string().nullable(),
  provider: z.string().nullable(),
  /** The newest assessment, when the status is "assessed". */
  assessedAt: z.string().nullable(),
  assessedByName: z.string().nullable(),
});
export type EarlierLabEvidence = z.infer<typeof earlierLabEvidenceSchema>;
